import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSettings } from "../lib/useSettings";

// Default fallback business data — used if /api/settings is unavailable.
const DEFAULTS = {
  business_name: "NorthCrest Landscaping",
  url: "https://northcrestlandscaping.ca",
  logo: "https://northcrestlandscaping.ca/logo.png",
  image: "https://images.unsplash.com/photo-1775733923991-e7223f9f44bc?crop=entropy&cs=srgb&fm=jpg&q=85",
  phone: "+1-780-655-7604",
  email: "northcrestedmonton@gmail.com",
  address_locality: "Edmonton",
  address_region: "AB",
  address_country: "CA",
  geo: { lat: 53.5461, lng: -113.4938 },
  instagram_url: "https://www.instagram.com/northcrestlandscaping.edmonton/",
  facebook_url: "https://www.facebook.com/profile.php?id=61590468096615",
  rating_value: "4.9",
  rating_count: 120,
  serviceAreas: [
    "Edmonton", "St. Albert", "Sherwood Park", "Beaumont", "Leduc", "Spruce Grove", "Fort Saskatchewan",
  ],
};

// Inject one JSON-LD script directly into <head>.
// react-helmet-async strips <script type="application/ld+json">, so we manage it manually.
function useJsonLd(id, payload) {
  useEffect(() => {
    if (!payload) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.seoId = id;
    el.textContent = JSON.stringify(payload);
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [id, JSON.stringify(payload)]);
}

// LocalBusiness schema — used on Home, Contact, About
export function LocalBusinessSchema() {
  const s = useSettings();
  const d = { ...DEFAULTS, ...(s || {}) };
  useJsonLd("local-business", {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: d.business_name,
    legalName: d.business_name,
    url: DEFAULTS.url,
    logo: DEFAULTS.logo,
    image: DEFAULTS.image,
    telephone: d.phone,
    email: d.email,
    priceRange: "$$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: d.address_locality,
      addressRegion: d.address_region,
      addressCountry: d.address_country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: DEFAULTS.geo.lat,
      longitude: DEFAULTS.geo.lng,
    },
    areaServed: DEFAULTS.serviceAreas.map((n) => ({ "@type": "City", name: n })),
    sameAs: [d.instagram_url, d.facebook_url].filter(Boolean),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: String(d.rating_value),
      reviewCount: Number(d.rating_count) || 0,
    },
  });
  return null;
}

// FAQPage schema — used on ServiceArea, AreaService pages
export function FAQSchema({ faqs }) {
  useJsonLd("faq-page", faqs && faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null);
  return null;
}

// Service schema (with area provided) — used on AreaService pages
export function ServiceSchema({ serviceName, areaName, description, imageUrl }) {
  useJsonLd("service", serviceName && areaName ? {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${serviceName} in ${areaName}`,
    serviceType: serviceName,
    provider: {
      "@type": "LocalBusiness",
      name: DEFAULTS.business_name,
      telephone: DEFAULTS.phone,
      url: DEFAULTS.url,
    },
    areaServed: { "@type": "City", name: areaName },
    description,
    image: imageUrl,
  } : null);
  return null;
}

// BreadcrumbList schema
export function BreadcrumbSchema({ items }) {
  useJsonLd("breadcrumb", items && items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  } : null);
  return null;
}

// Canonical URL — react-helmet-async also drops <link>, so inject manually
export function Canonical({ href }) {
  useEffect(() => {
    if (!href) return undefined;
    let el = document.head.querySelector("link[rel=canonical]");
    const created = !el;
    if (!el) {
      el = document.createElement("link");
      el.rel = "canonical";
      document.head.appendChild(el);
    }
    const prev = el.href;
    el.href = href;
    return () => {
      if (created && el.parentNode) el.parentNode.removeChild(el);
      else if (!created) el.href = prev;
    };
  }, [href]);
  return null;
}

// Re-export Helmet for callers who still want it (title/meta still work fine)
export { Helmet };
